/**
 * Formulario genérico.
 *
 * Se arma con `views.form` de la metadata: bloques, columnas, solapas y un campo
 * de estado en la cabecera. Igual que la lista, no tiene nada de ningún módulo.
 *
 * Cuatro decisiones que definen su comportamiento:
 *
 * 1. **Divulgación progresiva** (ARQUITECTURA.md §7): los campos con `priority`
 *    mayor a 1 no se muestran de entrada; viven detrás de "Opciones avanzadas".
 *    Un formulario de 40 campos donde 8 importan es la razón por la que un ERP
 *    necesita un consultor para dar de alta un proveedor.
 * 2. **Guardado optimista con vuelta atrás**: la pantalla refleja el cambio de
 *    inmediato y, si el backend lo rechaza, se restaura el valor anterior y se
 *    muestra el error del servidor campo por campo. Nunca queda mostrando un
 *    dato que no se guardó.
 * 3. **Solo se envía lo que cambió.** Un PATCH con los 40 campos pisaría lo que
 *    otro usuario tocó mientras tanto.
 * 4. **Aviso al salir con cambios sin guardar**, tanto al cerrar la pestaña como
 *    al navegar dentro de la aplicación.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FieldControl, getFieldInput, isEmpty, isMany2One, toDraftRows } from '@/components/fields';
import { modelPath, recordPath } from '@/components/shell/menu-utils';
import { Badge, Button, ErrorState, LoadingState } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import { ApiError, api } from '@/lib/api';
import type {
  Domain,
  FieldGroup,
  FieldMeta,
  FieldValue,
  JsonValue,
  ModelSpec,
  RecordData,
  RecordPayload,
  SmartButton,
} from '@/lib/types';
import { useActionItems } from '@/views/ActionBar';
import { AttachmentsPanel } from '@/views/AttachmentsPanel';
import { ChatterPanel } from '@/views/ChatterPanel';
import { ConfirmDialog } from '@/views/ConfirmDialog';
import { evaluateDomain } from '@/views/domain-eval';
import { esDefinitivo, motivoDe } from '@/views/errores';
import {
  AccionesNoDisponibles,
  AvisoDeBloqueo,
  MOTIVO_SUCIO,
  MOTIVO_SUCIO_IMPRIMIR,
  MOTIVO_SUCIO_MAIL,
  ProveedorDeGuardado,
  type AccionNoDisponible,
  type EstadoDeGuardado,
} from '@/views/MotivoBloqueo';
import { useReportItems } from '@/views/ReportBar';
import { useMailItems } from '@/views/SendMailDialog';
import { SmartButtons } from '@/views/SmartButtons';
import { TabPanel } from '@/views/tab-panels';
import { Toolbar, type ToolbarItem } from '@/views/Toolbar';
import { camposDeFicha, claveDeFicha, useCompanyId, useInvalidateModel, useRecordQuery } from '@/views/queries';
import { useDirtyGuard } from '@/views/useDirtyGuard';
import { validateRecord } from '@/views/validation';
import { amountBreakdown, splitByPriority } from '@/views/view-utils';

export interface FormViewProps {
  spec: ModelSpec;
  /** Id del registro, o `'new'` para uno nuevo. */
  recordId: number | 'new';
  /** Menu por el que se entro: se conserva al volver a la lista y en las migas. */
  menuName?: string | null;
  /** Contexto de la accion: `default_<campo>` precarga un registro nuevo. */
  context?: Record<string, JsonValue>;
}

type Valores = Record<string, FieldValue>;

/**
 * Extrae los botones inteligentes del detalle del registro. Viajan como una clave
 * más del JSON, fuera de los campos del modelo, así que se leen aparte y se
 * validan antes de confiar en su forma.
 */
function leerBotonesInteligentes(registro: RecordData | undefined): SmartButton[] {
  const crudo = registro?.smart_buttons;
  return Array.isArray(crudo) ? (crudo as unknown as SmartButton[]) : [];
}

/**
 * «a, b y c». Existe para no escribir tres frases distintas según qué secciones
 * tenga el modelo: la ficha de un contacto tiene adjuntos y seguimiento, la de
 * una línea de asiento no tiene ninguno.
 */
function listaEnCastellano(partes: string[]): string {
  if (partes.length <= 1) return partes[0] ?? '';
  return `${partes.slice(0, -1).join(', ')} ni ${partes[partes.length - 1] as string}`;
}

/** Bloques de la hoja principal cuando el módulo no declaró ninguno. */
function gruposPorDefecto(spec: ModelSpec): FieldGroup[] {
  const campos = Object.entries(spec.fields)
    .filter(([, meta]) => meta.store && meta.editable !== false)
    .sort((a, b) => a[1].priority - b[1].priority)
    .map(([nombre]) => nombre);
  return [{ label: null, fields: campos, columns: 2 }];
}

/**
 * Valores iniciales de un registro nuevo: los defaults del modelo, y encima los
 * `default_<campo>` del contexto de la acción por la que se entró.
 *
 * Es lo que hace que "Nuevo" desde el menú «Clientes» nazca con la bandera de
 * cliente marcada. Sin esto, el usuario crea desde una pantalla filtrada un
 * registro que esa misma pantalla no muestra, y parece que no se guardó.
 */
function valoresIniciales(spec: ModelSpec, contexto: Record<string, JsonValue> = {}): Valores {
  const salida: Valores = {};
  for (const [nombre, meta] of Object.entries(spec.fields)) {
    // Un many2one TAMBIEN puede traer default. Antes se forzaba a `null` y se
    // perdia: es lo que hacia que «Empresa» —obligatoria, y que el backend manda
    // precargada con la empresa activa— naciera vacia y «Crear» no hiciera nada.
    // El default de un many2one viaja como `{id, display_name}`, que es la misma
    // forma con la que se serializa el campo.
    if (meta.type === 'many2one') salida[nombre] = isMany2One(meta.default) ? meta.default : null;
    else if (meta.type === 'many2many' || meta.type === 'one2many') salida[nombre] = [];
    else if (meta.default !== undefined && meta.default !== null) salida[nombre] = meta.default;
    else if (meta.type === 'boolean') salida[nombre] = false;
    else salida[nombre] = null;
  }

  for (const [clave, valor] of Object.entries(contexto)) {
    if (!clave.startsWith('default_')) continue;
    const campo = clave.slice('default_'.length);
    const meta = spec.fields[campo];
    // Un default sobre un campo de solo lectura no se puede enviar: el backend
    // lo rechaza con un 400 y el usuario no tiene cómo corregirlo.
    if (meta === undefined || meta.readonly || meta.editable === false) continue;
    salida[campo] = valor;
  }
  return salida;
}

/**
 * Bloqueo condicional por estado del registro: `options.readonly_domain`.
 *
 * Es GENERICO y vive en la metadata, no en el frontend: cualquier modulo puede
 * declarar «este campo deja de editarse cuando el registro esta en tal estado»
 * con un dominio, y el renderer unico lo obedece. El dominio se evalua contra
 * los valores que hay EN PANTALLA con el mismo evaluador que usan las
 * decoraciones de la lista.
 *
 * Nacio de un defecto concreto: el cartel de «Emitir» del periodo de expensas
 * prometia que la liquidacion quedaba congelada y el formulario seguia dejando
 * escribir el mes, los vencimientos y la grilla de gastos. Ahora el backend se
 * niega (es la unica garantia real) y la pantalla lo refleja antes de que el
 * usuario tipee algo que va a rebotar.
 */
function bloqueadoPorDominio(meta: FieldMeta, valores: Valores, spec: ModelSpec): boolean {
  const dominio = meta.options?.['readonly_domain'];
  if (!Array.isArray(dominio) || dominio.length === 0) return false;
  return evaluateDomain(dominio as Domain, valores as unknown as RecordData, spec.fields);
}

/** El texto que explica POR QUE esta bloqueado, si el modulo declaro uno. */
function motivoDeBloqueo(meta: FieldMeta): string | null {
  const texto = meta.options?.['readonly_text'];
  return typeof texto === 'string' && texto !== '' ? texto : null;
}

/**
 * Por que este REGISTRO no se puede borrar, o `null` si se puede.
 *
 * Hermano de `bloqueadoPorDominio`, pero a nivel de registro y no de campo: el
 * modulo declara `delete_blocked_domain` en su `ModelSpec` y el servidor ya lo
 * hace cumplir (`ExpensePeriod.delete` contesta 403). Sin esto, el formulario de
 * un periodo emitido dibujaba «Eliminar» en rojo justo al lado del cartel que
 * dice que la liquidacion quedo congelada: dos afirmaciones contrarias en la
 * misma pantalla, y la que el usuario prueba es la que falla.
 */
function motivoDeBorradoBloqueado(spec: ModelSpec, valores: Valores): string | null {
  const dominio = spec.delete_blocked_domain;
  if (!Array.isArray(dominio) || dominio.length === 0) return null;
  if (!evaluateDomain(dominio as Domain, valores as unknown as RecordData, spec.fields)) return null;
  const texto = spec.delete_blocked_text;
  return typeof texto === 'string' && texto !== '' ? texto : 'Este registro no se puede eliminar.';
}

/**
 * Lo que se manda al backend: solo los campos indicados, con los many2one
 * reducidos a su id.
 *
 * Se saltean los de solo lectura y los no editables porque el backend los
 * rechaza con un 400 (`_apply_payload`), y hacerle pegar ese viaje al usuario
 * para enterarse de algo que ya sabemos por la metadata no tiene sentido.
 *
 * Las grillas hijas (`one2many`) viajan SOLO al crear, y solo si traen filas
 * cargadas en memoria: el POST las escribe junto con la cabecera en una sola
 * transaccion (ver `_create_child_rows` en `core/api.py`). Es lo que permite
 * cargar los renglones de un presupuesto sin haberlo guardado antes. En un
 * PATCH la grilla sigue guardandose fila por fila contra el modelo hijo, que es
 * donde ese problema no existe.
 */
function construirPayload(
  spec: ModelSpec,
  valores: Valores,
  campos: Set<string>,
  conGrillas: boolean,
): RecordPayload {
  const payload: RecordPayload = {};
  for (const nombre of campos) {
    const meta = spec.fields[nombre];
    if (meta === undefined) continue;
    if (meta.readonly || meta.editable === false) continue;

    if (meta.type === 'one2many') {
      if (!conGrillas) continue;
      const filas = toDraftRows(valores[nombre] ?? null);
      if (filas.length === 0) continue;
      payload[nombre] = filas.map((fila) => {
        // Cada celda viaja como la manda cualquier otro control: un many2one,
        // reducido a su id.
        const salida: Record<string, JsonValue> = {};
        for (const [campo, valor] of Object.entries(fila)) {
          salida[campo] = (isMany2One(valor) ? valor.id : valor) as JsonValue;
        }
        return salida;
      });
      continue;
    }

    const valor = valores[nombre] ?? null;
    payload[nombre] = meta.type === 'many2one' && isMany2One(valor) ? valor.id : valor;
  }
  return payload;
}

export function FormView({ spec, recordId, menuName = null, context = {} }: FormViewProps) {
  const esNuevo = recordId === 'new';
  const idRegistro = esNuevo ? null : recordId;

  const navegar = useNavigate();
  const cliente = useQueryClient();
  const companyId = useCompanyId();
  const invalidar = useInvalidateModel();

  // LOS CAMPOS QUE SE PIDEN, y no «todos».
  //
  // Se piden todos MENOS las relaciones inversas. Cada una le cuesta al servidor
  // una consulta propia —la ficha de un contacto tiene 27 y eran 24 de sus 44
  // consultas— y en un registro ya guardado no las usa nadie: la grilla
  // (`One2ManyField`) pide sus filas por su cuenta apenas hay un id del que
  // colgar. La lista de ids del padre sólo sirve en un ALTA, y en un alta no hay
  // registro que leer.
  //
  // Sale de la metadata, que es la misma fuente con la que el formulario decide
  // qué dibujar: si mañana aparece un campo nuevo, entra solo.
  const camposQueSePiden = useMemo(() => camposDeFicha(spec), [spec.fields]);
  const consulta = useRecordQuery(spec.model, idRegistro, camposQueSePiden);
  const [valores, setValores] = useState<Valores>(() => (esNuevo ? valoresIniciales(spec, context) : {}));
  const [sucios, setSucios] = useState<Set<string>>(() => new Set());
  const [errores, setErrores] = useState<Record<string, string>>({});
  /**
   * Por qué el servidor rechazó el guardado, cuando el motivo NO es de un campo.
   *
   * Existe porque sin esto el rechazo desaparecía. El caso testigo: cargar en
   * «Contactos › Nuevo» un CUIT que ya estaba en otra ficha. El backend contesta
   * 400 con el motivo escrito entero —«Ese CUIT ya está cargado en otro contacto
   * de esta organización…»— bajo la clave `non_field_errors`, que es la de DRF
   * para un error **del registro** y no de una columna. El formulario recorría
   * los errores buscando a qué control pintarlos, no encontraba ninguno con ese
   * nombre, y se quedaba con las manos vacías: ni campo marcado ni cartel. Desde
   * afuera «Crear» parece un botón roto.
   *
   * Se guarda en estado y se dibuja fijo arriba del formulario, no en un aviso
   * que se va solo: el motivo hay que poder releerlo mientras se corrige el dato.
   */
  const [motivoDelRechazo, setMotivoDelRechazo] = useState<string | null>(null);
  const [confirmarBorrado, setConfirmarBorrado] = useState(false);
  /**
   * Por qué el borrado no salió, dicho DENTRO del diálogo que lo pidió.
   *
   * El caso testigo: eliminar un contacto que tiene comprobantes. El servidor
   * contesta 409 con el motivo entero y la salida escrita —«…archivalo: se saca
   * de las listas y lo que ya se emitió sigue en pie»—, y la pantalla lo tiraba
   * en un aviso flotante en la esquina, detrás del modal, mientras el diálogo
   * seguía abierto con «Eliminar definitivamente» armado. O sea: el sistema
   * invitaba a repetir, tres veces seguidas, algo que nunca iba a andar.
   *
   * `definitivo` decide si el botón se retira (ver `views/errores`).
   */
  const [errorDeBorrado, setErrorDeBorrado] = useState<{ motivo: string; definitivo: boolean } | null>(
    null,
  );
  const [avanzadasAbiertas, setAvanzadasAbiertas] = useState(false);
  const [solapa, setSolapa] = useState(0);
  // LAS SOLAPAS SE MONTAN AL VISITARLAS, y quedan montadas. Cada grilla
  // embebida (`One2ManyField`) pide sus filas al montarse: con todas las
  // solapas montadas desde el principio, abrir la ficha de un consorcio
  // disparaba un pedido por grilla —medio segundo fijo cada uno— para solapas
  // que nadie iba a mirar. Una vez visitada no se desmonta: lo que se editó en
  // una grilla no puede perderse por cambiar de solapa.
  const [visitadas, setVisitadas] = useState<ReadonlySet<number>>(() => new Set([0]));
  useEffect(() => {
    setVisitadas((actual) => (actual.has(solapa) ? actual : new Set(actual).add(solapa)));
  }, [solapa]);
  const formulario = useRef<HTMLFormElement>(null);
  /**
   * Lo que hay que hacer DESPUÉS de que el guardado vuelva bien.
   *
   * Es el motor de «Guardar y seguir», y es un `ref` y no un estado porque tiene
   * que leerse dentro del `onSuccess` de la mutación sin provocar un render de
   * por medio. Se limpia SIEMPRE —al usarlo y al fallar—: una acción que se
   * queda colgada acá se dispararía en el próximo guardado, que es exactamente
   * la clase de sorpresa que este trabajo no puede introducir.
   */
  const trasGuardar = useRef<(() => void) | null>(null);
  /**
   * Lo que los CONTROLES del formulario dejaron anotado para después del
   * guardado. Ver `EstadoDeGuardado.alGuardar`.
   *
   * Es un `ref` por el mismo motivo que `trasGuardar` —se lee dentro del
   * `onSuccess` de la mutación— y es un `Set` porque puede haber más de un
   * control anotado. Se vacía SIEMPRE al usarlo: cada anotación corre una vez.
   */
  const anotadosPosGuardado = useRef<Set<(registro: RecordData) => void>>(new Set());
  /** Clave del botón al que devolverle el foco cuando se destrabe. */
  const [focoAlVolver, setFocoAlVolver] = useState<string | null>(null);

  const guardia = useDirtyGuard(sucios.size > 0);

  const registroServidor = consulta.data;
  // Los botones inteligentes viajan en el detalle del registro (no en la
  // metadata), porque el conteo depende del registro concreto. Un registro nuevo
  // todavía no existe, así que no trae ninguno y la fila no se dibuja.
  const botonesInteligentes = leerBotonesInteligentes(registroServidor);

  // El registro que llega del servidor es la base; encima van los cambios locales.
  // Solo corre al cambiar el registro traído: reaplicarlo en cada render pisaría
  // lo que el usuario está tipeando.
  useEffect(() => {
    if (registroServidor === undefined) return;
    setValores((actual) => {
      const base: Valores = { ...registroServidor };
      for (const campo of sucios) base[campo] = actual[campo] ?? null;
      return base;
    });
  }, [registroServidor]);

  const vista = spec.views.form;
  const gruposPrincipales = vista?.groups.length ? vista.groups : gruposPorDefecto(spec);
  const solapas = vista?.tabs ?? [];
  const campoEstado = vista?.header_field ?? null;

  const camposDelFormulario = useMemo(() => {
    const nombres = new Set<string>();
    for (const grupo of gruposPrincipales) for (const campo of grupo.fields) nombres.add(campo);
    for (const tab of solapas) for (const grupo of tab.groups) for (const campo of grupo.fields) nombres.add(campo);
    if (campoEstado !== null) nombres.add(campoEstado);
    return [...nombres].filter((nombre) => spec.fields[nombre] !== undefined);
  }, [gruposPrincipales, solapas, campoEstado, spec]);

  const soloLectura = !(esNuevo ? spec.permissions.create : spec.permissions.write);

  const cambiar = (campo: string, valor: FieldValue): void => {
    setValores((actual) => ({ ...actual, [campo]: valor }));
    setSucios((actual) => new Set(actual).add(campo));
    setErrores((actual) => {
      if (actual[campo] === undefined) return actual;
      const copia = { ...actual };
      delete copia[campo];
      return copia;
    });
  };

  /**
   * Escribir VARIOS campos de una, para los widgets que lo declararon.
   *
   * Es la única capacidad de framework que agrega el selector de producto: al
   * elegir un trámite del catálogo hay que completar el detalle, el precio, el
   * importe, el vencimiento y la frecuencia **a la vez y delante del usuario**.
   * Hacerlo con cinco `cambiar()` seguidos perdería cuatro: cada uno parte del
   * estado que el render tenía, no del acumulado.
   *
   * Es un `cambiar` de a varios y nada más: marca sucios los mismos campos y
   * limpia los mismos errores. Quién lo recibe lo decide una lista blanca por
   * nombre de widget (`widgetUsaHermanos`), no este formulario.
   */
  const cambiarVarios = (parcial: Record<string, FieldValue>): void => {
    const campos = Object.keys(parcial);
    if (campos.length === 0) return;
    setValores((actual) => ({ ...actual, ...parcial }));
    setSucios((actual) => {
      const copia = new Set(actual);
      for (const campo of campos) copia.add(campo);
      return copia;
    });
    setErrores((actual) => {
      if (!campos.some((campo) => actual[campo] !== undefined)) return actual;
      const copia = { ...actual };
      for (const campo of campos) delete copia[campo];
      return copia;
    });
  };

  // LA MISMA CLAVE que usa `useRecordQuery` de arriba, campos incluidos. Antes
  // se armaba sin los campos y lo que se escribía acá —el optimista, la
  // respuesta del guardado— iba a una clave que nadie leía: la ficha se veía
  // vieja hasta que llegaba la relectura, y la relectura era un pedido de más.
  const claveRegistro = claveDeFicha(companyId, spec.model, idRegistro ?? 0, camposQueSePiden);

  const guardar = useMutation<RecordData, Error, RecordPayload, { previo: RecordData | undefined }>({
    // Los mismos campos que al leer: la respuesta del guardado es la ficha.
    mutationFn: (payload) =>
      esNuevo
        ? api.createRecord(spec.model, payload, camposQueSePiden)
        : api.updateRecord(spec.model, idRegistro as number, payload, camposQueSePiden),

    // Optimista: la caché muestra el cambio antes de que el servidor conteste.
    onMutate: async (payload) => {
      if (esNuevo) return { previo: undefined };
      await cliente.cancelQueries({ queryKey: claveRegistro });
      const previo = cliente.getQueryData<RecordData>(claveRegistro);
      if (previo !== undefined) {
        cliente.setQueryData<RecordData>(claveRegistro, { ...previo, ...payload } as RecordData);
      }
      return { previo };
    },

    onError: (error, _payload, contexto) => {
      // Si el guardado falló, lo que venía después NO corre. Encadenar sobre un
      // registro que no se guardó ejecutaría la acción contra datos viejos, que
      // es justo lo que el bloqueo estaba evitando. Lo mismo vale para lo que
      // anotaron los controles: el registro sigue como estaba, así que lo que
      // había que hacerle encima no tiene sobre qué correr.
      trasGuardar.current = null;
      anotadosPosGuardado.current.clear();
      setFocoAlVolver(null);
      // Vuelta atrás: la pantalla no puede quedar mostrando lo que no se guardó.
      if (contexto?.previo !== undefined) cliente.setQueryData(claveRegistro, contexto.previo);

      const delServidor: Record<string, string> = {};
      // Lo que NO se pudo colgar de ningún control. Se junta acá y se dibuja
      // arriba del formulario: un motivo que el servidor se tomó el trabajo de
      // escribir no puede terminar sólo en la consola del navegador.
      const sinCampo: string[] = [];
      if (error instanceof ApiError && Object.keys(error.fields).length > 0) {
        for (const [campo, mensajes] of Object.entries(error.fields)) {
          const texto = mensajes.join(' ');
          // Un error del registro entero (`non_field_errors`) no es de ningun
          // control: marcarlo dejaria un mensaje colgado de un campo que no
          // existe y `enfocar` iria a buscar un nodo que no esta.
          if (spec.fields[campo] === undefined) {
            sinCampo.push(texto);
            continue;
          }
          delServidor[campo] = texto;
        }
      }
      setErrores(delServidor);
      const primero = Object.keys(delServidor)[0];
      if (primero !== undefined) enfocar(primero);
      // El cartel de arriba se dibuja SIEMPRE, pase lo que pase. Las tres
      // fuentes, en orden: el motivo del registro entero, el resumen de los
      // campos que fallaron, y el mensaje pelado si no hubo ni una cosa ni la
      // otra (o si el error ni siquiera es de la API). Antes, un 400 que
      // nombraba un campo dejaba el cartel en `null` y todo el rastro del
      // rechazo era un borde rojo que podía estar en otra solapa.
      setMotivoDelRechazo(
        sinCampo.length > 0
          ? sinCampo.join(' ')
          : (resumenDeProblemas(delServidor) ?? error.message),
      );
      notify.error('No se pudo guardar', error.message);
    },

    onSuccess: (registro) => {
      setSucios(new Set());
      setErrores({});
      setMotivoDelRechazo(null);
      setValores({ ...registro });
      // La respuesta del guardado ES la ficha (releída y con sus contadores):
      // queda en la caché bajo su clave y no se vuelve a pedir. En un alta es
      // lo que hace que la pantalla del registro nuevo aparezca sin esperar.
      cliente.setQueryData(claveDeFicha(companyId, spec.model, registro.id, camposQueSePiden), registro);
      void invalidar(spec.model, registro.id);
      // «Se creó: Factura» y no «Factura creado»: la etiqueta del modelo no
      // declara género, así que concordar el participio con ella es imposible
      // en el 50 % de los casos. Los dos puntos evitan el problema entero.
      notify.success(esNuevo ? `Se creó: ${spec.label}` : 'Cambios guardados');

      // Lo que anotaron los controles, con el registro ya guardado en la mano.
      // Corre ANTES de navegar en un alta: la pantalla se va a reemplazar por
      // la del registro nuevo y el control que anotó se va a desmontar, pero la
      // función ya está en vuelo y sabe su id. Ver `EstadoDeGuardado.alGuardar`.
      const anotados = [...anotadosPosGuardado.current];
      anotadosPosGuardado.current.clear();
      for (const anotado of anotados) anotado(registro);

      if (esNuevo) {
        navegar(recordPath(spec.model, registro.id, menuName), { replace: true });
        // En un alta no se encadena nada: la pantalla se está reemplazando por
        // la del registro recién creado y las acciones se piden con su id.
        trasGuardar.current = null;
        return;
      }
      // «Guardar y seguir»: la acción que la persona quería, ahora que el
      // registro guardado y lo que ve en pantalla son lo mismo. Sólo llega
      // acá lo que NO escribe nada (ver `sePuedeEncadenar` en `ActionBar`);
      // todo lo que emite deja el botón listo y espera el clic.
      const pendiente = trasGuardar.current;
      trasGuardar.current = null;
      if (pendiente !== null) pendiente();
    },
  });

  const borrar = useMutation({
    mutationFn: () => api.deleteRecord(spec.model, idRegistro as number),
    onSuccess: () => {
      setSucios(new Set());
      setErrorDeBorrado(null);
      setConfirmarBorrado(false);
      void invalidar(spec.model);
      notify.success(`Se eliminó: ${spec.label}`);
      navegar(modelPath(spec.model, menuName), { replace: true });
    },
    onError: (error) => {
      // El diálogo QUEDA ABIERTO y se hace cargo del motivo: es la pantalla que
      // la persona está mirando. No se dispara además un aviso flotante —sería
      // el mismo texto dos veces, y el de la esquina queda tapado por el modal—.
      setErrorDeBorrado({ motivo: motivoDe(error, 'No se pudo eliminar.'), definitivo: esDefinitivo(error) });
    },
  });

  const archivar = useMutation({
    mutationFn: (archivado: boolean) =>
      api.archiveRecord(spec.model, idRegistro as number, archivado, camposQueSePiden),
    onSuccess: (registro) => {
      setValores({ ...registro });
      // Archivar es la salida que ofrece el diálogo de borrado bloqueado: si
      // salió bien, ese diálogo ya no tiene nada que explicar.
      setConfirmarBorrado(false);
      setErrorDeBorrado(null);
      cliente.setQueryData(claveRegistro, registro);
      void invalidar(spec.model, idRegistro as number);
      notify.success(registro.active === false ? 'Registro archivado' : 'Registro restaurado');
    },
    onError: (error) => {
      notify.error('No se pudo cambiar el archivado', motivoDe(error));
    },
  });

  // -------------------------------------------------------------------------
  // Botonera de la cabecera
  // -------------------------------------------------------------------------
  /*
    Las tres barras del encabezado —acciones de servidor, reportes y correo—
    ya no se dibujan cada una por su cuenta: entregan sus botones DESCRITOS y
    acá se arma una sola botonera que pliega lo que sobra en un único menú
    «Más» (ver `Toolbar`). Antes eran tres componentes hermanos y, en un
    período de expensas emitido, ocho botones a la misma altura que el título:
    el `<h1>` quedaba con 174 px para 185 y el nombre del registro se leía
    «Expensas 07/2…».

    Los hooks se llaman SIEMPRE, incluso en un registro nuevo —donde no hay id
    contra el que ejecutar nada—, porque un hook condicionado rompe el orden
    entre renders. Lo que se condiciona es la lista de botones, que en ese caso
    queda vacía.
  */
  const esRegistro = typeof recordId === 'number';
  const bloqueoPorSucios = sucios.size > 0;

  /**
   * La salida que la ficha le presta a todo lo que cuelga de ella.
   *
   * Es lo que hace que «Guardá el registro para poder…» deje de ser una frase y
   * pase a ser un botón: los botones de la cabecera, la grilla de renglones, el
   * panel de venta rápida, los adjuntos y el seguimiento ofrecen exactamente el
   * mismo gesto con exactamente el mismo texto, porque salen todos de acá.
   *
   * `guardar` NO ejecuta nada por su cuenta: si le pasan un `continuar`, lo
   * guarda para después del guardado, y quien decide si eso es legítimo es
   * `sePuedeEncadenar` en `ActionBar` —jamás algo que emita—.
   *
   * El pedido viaja hasta `enviar`, que lo arma **en la misma línea en que manda
   * el guardado** y no antes. Esa es la diferencia entre una protección y una
   * intención: armado antes, un guardado que no sale —falta un obligatorio, el
   * formulario frena y nombra el campo— dejaba el pedido colgado y lo disparaba
   * el guardado siguiente, hecho por otra cosa y en otro momento. Encadenar
   * cuando nadie lo pidió es el mismo defecto que encadenar una emisión, dicho
   * al revés.
   */
  const estadoDeGuardado: EstadoDeGuardado = {
    esNuevo,
    haySucios: bloqueoPorSucios,
    guardando: guardar.isPending,
    guardar: (opciones) => {
      enviar({
        continuar: opciones?.continuar ?? null,
        devolverFocoA: opciones?.devolverFocoA ?? null,
      });
    },
    alGuardar: (accion) => {
      const anotado = accion as (registro: RecordData) => void;
      anotadosPosGuardado.current.add(anotado);
      return () => {
        anotadosPosGuardado.current.delete(anotado);
      };
    },
  };

  const accionesDeServidor = useActionItems({
    spec,
    ids: esRegistro ? [recordId] : [],
    scope: 'record',
    // Con cambios sin guardar se bloquean: la acción corre sobre lo que hay en
    // la base, no sobre lo que se ve en pantalla, y liquidar un período con
    // importes a medio editar es un error caro. Lo que cambió no es el bloqueo
    // —sigue igual— sino que ahora se dice, donde la persona lo busca, y se
    // ofrece la salida.
    blockedReason: bloqueoPorSucios ? MOTIVO_SUCIO : null,
    guardado: estadoDeGuardado,
  });

  const reportes = useReportItems({
    spec,
    recordId: esRegistro ? recordId : 0,
    // Mismo motivo que las acciones: el PDF se arma con lo que hay en la base, y
    // un comprobante a medio editar imprimiría números que no son.
    blockedReason: bloqueoPorSucios ? MOTIVO_SUCIO_IMPRIMIR : null,
    guardado: estadoDeGuardado,
  });

  const correo = useMailItems({
    spec,
    recordId: esRegistro ? recordId : 0,
    // Ídem: el adjunto es el PDF del registro guardado, así que mandarlo a medio
    // editar es adjuntarle al cliente números que no son.
    blockedReason: bloqueoPorSucios ? MOTIVO_SUCIO_MAIL : null,
    guardado: estadoDeGuardado,
  });

  /**
   * En qué solapa vive un campo: `-1` = la hoja principal, `0..n` = la solapa.
   *
   * Hace falta porque un campo obligatorio puede estar en una solapa que no es
   * la que se está mirando, y entonces marcarlo con un mensaje rojo no le sirve
   * a nadie: el mensaje se dibuja dentro de un panel con `hidden`.
   */
  const solapaDelCampo = (campo: string): number => {
    for (const [indice, tab] of solapas.entries()) {
      for (const grupo of tab.groups) {
        if (grupo.fields.includes(campo)) return indice;
      }
    }
    return -1;
  };

  /**
   * El cartel de arriba cuando lo que falló es de UNO O VARIOS CAMPOS.
   *
   * Hasta acá, un 400 que nombraba un campo pintaba el control de rojo y nada
   * más. Suena suficiente y no lo es: si el campo vive en otra solapa, o abajo
   * de todo, o detrás de «Opciones avanzadas», el único rastro visible del
   * rechazo era un puntito al lado del nombre de la solapa. El cartel dice el
   * QUÉ y el DÓNDE, y se puede releer mientras se corrige; el foco (`enfocar`)
   * lleva hasta ahí.
   *
   * Nombra el campo con su etiqueta visible —nunca con la clave técnica— y
   * repite textual el mensaje del servidor: acá no se redacta ninguna regla.
   */
  const resumenDeProblemas = (problemas: Record<string, string>): string | null => {
    const entradas = Object.entries(problemas);
    const primera = entradas[0];
    if (primera === undefined) return null;
    const [campo, mensaje] = primera;
    const etiqueta = spec.fields[campo]?.label ?? campo;
    const indice = solapaDelCampo(campo);
    const nombreSolapa = indice >= 0 ? (solapas[indice]?.label ?? '') : '';
    const donde = nombreSolapa === '' ? '' : ` (está en la solapa «${nombreSolapa}»)`;
    const resto =
      entradas.length > 1
        ? ` Hay ${entradas.length} campos con problemas: están marcados en rojo.`
        : '';
    return `«${etiqueta}»${donde}: ${mensaje}${resto}`;
  };

  /**
   * Lleva al usuario hasta el campo: abre su solapa, despliega «Opciones
   * avanzadas» si está adentro, le da el foco y lo trae a la vista.
   *
   * Sin esto, «Crear» con un obligatorio escondido **no hacía nada visible**: no
   * salía petición (bien, falta un dato) pero tampoco se veía el error, porque
   * el único rastro era un «Este dato es obligatorio.» dentro de un panel
   * `hidden` —alto 0— o de un bloque colapsado. El caso real: «Empresa» en el
   * formulario de cobros, obligatoria, en la solapa «Contabilidad».
   */
  const enfocar = (campo: string): void => {
    const destino = solapaDelCampo(campo);
    if (destino >= 0) setSolapa(destino);
    // Los avanzados de la hoja principal son un bloque controlado por estado;
    // los de una solapa son un `<details>`, que se abre más abajo sobre el DOM.
    if (destino === -1 && (spec.fields[campo]?.priority ?? 1) > 1) setAvanzadasAbiertas(true);

    // En el próximo cuadro: el cambio de solapa y el despliegue son estado de
    // React, así que el nodo todavía no está montado (o sigue `hidden`) en este.
    window.requestAnimationFrame(() => {
      const nodo = formulario.current?.querySelector<HTMLElement>(`#campo-${CSS.escape(campo)}`);
      if (nodo === null || nodo === undefined) return;
      // Cualquier `<details>` por encima (los avanzados de una solapa) se abre:
      // un campo dentro de un plegable cerrado no se puede enfocar ni ver.
      let ancestro: HTMLElement | null = nodo.parentElement;
      while (ancestro !== null) {
        if (ancestro instanceof HTMLDetailsElement) ancestro.open = true;
        ancestro = ancestro.parentElement;
      }
      nodo.focus();
      nodo.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  };

  /**
   * Manda el guardado, si hay algo que mandar.
   *
   * `pendiente` es lo que quedó esperando del otro lado de un control
   * bloqueado. Se arma **recién cuando la mutación sale**: los dos cortes de
   * abajo —falta un dato, no hay cambios— vuelven sin tocarlo, así que un
   * guardado que no salió no deja nada armado para el próximo.
   */
  const enviar = (
    pendiente: { continuar?: (() => void) | null; devolverFocoA?: string | null } = {},
  ): void => {
    const problemas = validateRecord(spec, valores, camposDelFormulario);
    setErrores(problemas);
    // El motivo del rechazo anterior no sobrevive a un intento nuevo: dejarlo
    // haria leer como actual un motivo que quizas ya se corrigio.
    setMotivoDelRechazo(null);
    const primero = Object.keys(problemas)[0];
    if (primero !== undefined) {
      enfocar(primero);
      // Mismo cartel que para un rechazo del servidor: para la persona que
      // apretó «Crear» y no pasó nada, que el freno lo haya puesto la pantalla o
      // el servidor es una distinción que no le interesa ni tiene por qué
      // notar. Y el toast se va solo; el cartel se queda mientras corrige.
      setMotivoDelRechazo(resumenDeProblemas(problemas));
      const etiqueta = spec.fields[primero]?.label ?? primero;
      // El toast NOMBRA el campo: «Revisá los campos marcados» a secas no sirve
      // cuando el campo marcado está en otra solapa.
      notify.warning(
        `Falta completar «${etiqueta}»`,
        Object.keys(problemas).length > 1
          ? `Hay ${Object.keys(problemas).length} campos sin completar o mal escritos.`
          : (problemas[primero] ?? 'Revisá el campo marcado.'),
      );
      return;
    }
    if (sucios.size === 0 && !esNuevo) {
      notify.info('No hay cambios para guardar');
      return;
    }

    // Al crear se manda lo que el usuario cargó mas los defaults con valor: un
    // `null` en un campo que no admite nulos seria un 400 evitable.
    const campos = esNuevo
      ? new Set(camposDelFormulario.filter((nombre) => sucios.has(nombre) || !isEmpty(valores[nombre] ?? null)))
      : sucios;

    trasGuardar.current = pendiente.continuar ?? null;
    setFocoAlVolver(pendiente.devolverFocoA ?? null);
    guardar.mutate(construirPayload(spec, valores, campos, esNuevo));
  };

  if (!esNuevo && consulta.isPending) return <LoadingState label={`Cargando ${spec.label.toLowerCase()}…`} />;

  if (!esNuevo && consulta.isError) {
    return (
      <ErrorState
        // Dos puntos y no artículo: «No se pudo abrir factura» no se puede leer,
        // y «la factura / el comprobante» exigiría que la metadata declarara
        // género en los 99 modelos del ERP.
        title={`No se pudo abrir: ${spec.label}`}
        message={consulta.error.message}
        onRetry={() => {
          void consulta.refetch();
        }}
      />
    );
  }

  const nombreActual = valores[spec.rec_name];
  const titulo = esNuevo
    ? `Nuevo: ${spec.label.toLowerCase()}`
    : typeof nombreActual === 'string' && nombreActual.trim() !== ''
      ? nombreActual
      : (registroServidor?.display_name ?? `${spec.label} #${recordId}`);

  const archivado = valores.active === false;

  /**
   * Archivar existe para este modelo: hay campo `active` y permiso de escritura.
   *
   * Es la salida real de casi todo borrado bloqueado en un ERP contable —lo que
   * ya se emitió no se puede borrar, pero sí se puede sacar de las listas— y por
   * eso el diálogo la ofrece como BOTÓN en vez de nombrarla dentro de un texto.
   */
  const sePuedeArchivar = spec.permissions.write && spec.fields.active !== undefined && !archivado;

  const renderCampo = (nombre: string, aclaracion: string | null = null) => {
    const meta = spec.fields[nombre];
    if (meta === undefined) return null;
    return (
      <FieldControl
        key={nombre}
        name={nombre}
        meta={meta}
        value={valores[nombre] ?? null}
        readonly={soloLectura || bloqueadoPorDominio(meta, valores, spec)}
        error={errores[nombre] ?? null}
        context={{ modelName: spec.model, recordId: idRegistro }}
        siblings={{ values: valores, setValues: cambiarVarios }}
        {...(aclaracion === null ? {} : { hint: aclaracion })}
        onChange={(valor) => {
          cambiar(nombre, valor);
        }}
      />
    );
  };

  // Los motivos DISTINTOS por los que hay campos bloqueados ahora mismo. Se
  // muestran una sola vez arriba del formulario y no campo por campo: repetir
  // «el periodo esta emitido» en los seis controles bloqueados es ruido, y el
  // usuario necesita leerlo antes de intentar escribir, no al lado de cada uno.
  const borradoBloqueado = esNuevo ? null : motivoDeBorradoBloqueado(spec, valores);

  const motivosDeBloqueo = [
    ...new Set(
      camposDelFormulario
        .map((nombre) => spec.fields[nombre])
        .filter((meta): meta is FieldMeta => meta !== undefined)
        .filter((meta) => bloqueadoPorDominio(meta, valores, spec))
        .map((meta) => motivoDeBloqueo(meta))
        .filter((texto): texto is string => texto !== null),
    ),
  ];

  /**
   * Todo lo que hoy NO se puede hacer sobre este registro, con su motivo.
   *
   * Junta las tres barras —acciones de servidor, reportes, correo— más el
   * borrado, que hasta ahora era el único caso explicado del sistema y lo estaba
   * en el lugar equivocado: el cartel salía arriba del formulario, a media
   * pantalla del lugar donde la persona buscaba el botón «Eliminar» y mezclado
   * con otros tres o cuatro carteles que hablan de campos. Acá queda al lado de
   * los demás botones que faltan, que es donde se lo busca.
   *
   * El motivo lo escribe el SERVIDOR en los tres primeros casos (`disabled_reason`)
   * y el `ModelSpec` en el del borrado (`delete_blocked_text`, que el backend
   * obliga a declarar desde siempre). El frontend no redacta ni una regla de
   * negocio.
   */
  /** Qué secciones de la ficha todavía no existen porque el registro no existe. */
  const faltaPorCrear: string[] = esNuevo
    ? [
        // Los botones inteligentes van SIEMPRE en la lista de un alta: cuentan
        // registros relacionados y viajan en el detalle del registro, así que
        // desde acá no hay forma de saber si el modelo tiene o no —y no
        // nombrarlos dejaría sin explicar la fila que más se nota que falta—.
        'los accesos a lo relacionado',
        ...(spec.attachments === true ? ['los archivos adjuntos'] : []),
        ...(spec.chatter === true ? ['el seguimiento'] : []),
      ]
    : [];

  const noDisponibles: AccionNoDisponible[] = esRegistro
    ? [
        ...accionesDeServidor.ocultas,
        ...reportes.ocultas,
        ...correo.ocultas,
        ...(spec.permissions.delete && borradoBloqueado !== null
          ? [{ clave: 'eliminar', etiqueta: 'Eliminar', motivo: borradoBloqueado }]
          : []),
      ]
    : [];

  /**
   * Los botones de la cabecera, en orden de importancia.
   *
   * El orden NO es una lista de casos escrita acá: las acciones del módulo
   * llegan ordenadas por su `sequence` desde la metadata, y los verbos propios
   * de la pantalla —archivar y eliminar— van último a propósito, que es donde
   * corresponde que estén. `Toolbar` deja los primeros a la vista y pliega el
   * resto.
   */
  const botonesDeLaCabecera: ToolbarItem[] = esRegistro
    ? [
        ...accionesDeServidor.items,
        ...reportes.items,
        ...correo.items,
        ...(spec.permissions.write && spec.fields.active !== undefined
          ? [
              {
                key: 'archivar',
                label: archivado ? 'Restaurar' : 'Archivar',
                loading: archivar.isPending,
                onClick: () => {
                  archivar.mutate(!archivado);
                },
              },
            ]
          : []),
        // «Eliminar» desaparece cuando el registro no se puede borrar (ver
        // `motivoDeBorradoBloqueado`). No se deja gris: un boton deshabilitado
        // al lado del cartel de congelado invita a probarlo igual. El motivo se
        // dice arriba, junto con los demas motivos de bloqueo.
        ...(spec.permissions.delete && borradoBloqueado === null
          ? [
              {
                key: 'eliminar',
                label: 'Eliminar',
                variant: 'danger' as const,
                onClick: () => {
                  // El motivo del intento anterior no sobrevive a uno nuevo:
                  // dejarlo haría leer como actual algo que quizás ya cambió.
                  setErrorDeBorrado(null);
                  setConfirmarBorrado(true);
                },
              },
            ]
          : []),
      ]
    : [];

  // El campo de estado se dibuja UNA sola vez: como semáforo en la cabecera
  // (`CabeceraEstado`). Si además figura dentro de un bloque del schema, se lo
  // saca de ahí para no repetir el mismo control dos veces en el formulario
  // —era el caso del pedido de venta, con el estado en la barra y en el bloque 1—.
  const sinEstado = (campos: string[]): string[] =>
    campoEstado === null ? campos : campos.filter((nombre) => nombre !== campoEstado);

  const renderGrupo = (grupo: FieldGroup, indice: number, mostrarAvanzados: boolean) => {
    // BLOQUES QUE SOLO APLICAN A ALGUNOS REGISTROS. El dominio se evalua contra
    // lo que hay cargado AHORA en el formulario —no contra lo guardado— asi que
    // el bloque aparece en el momento en que se elige el producto que lo pide,
    // sin guardar ni recargar.
    //
    // Mismo evaluador que las decoraciones de la lista: una sola gramatica de
    // dominios en todo el frontend.
    // Se evalúa contra `valores` —el borrador— y no contra la fila del
    // servidor: así el bloque aparece en el momento en que se elige el producto
    // que lo pide, sin guardar ni recargar. Mismo `as unknown as RecordData`
    // que `bloqueadoPorDominio` de más arriba, y por el mismo motivo: al
    // borrador le faltan `id` y `display_name`, que el evaluador no mira.
    if (
      grupo.visible_if != null &&
      !evaluateDomain(grupo.visible_if, valores as unknown as RecordData, spec.fields)
    ) {
      return null;
    }

    const { basicos, avanzados } = splitByPriority(spec, sinEstado(grupo.fields));
    const visibles = mostrarAvanzados ? avanzados : basicos;
    if (visibles.length === 0) return null;

    // En un bloque de importes derivados, cuál de ellos es la suma de los otros.
    // Es lo que evita que «Total $ 11.410.905» y «Facturado $ 9.430.500», uno al
    // lado del otro, se lean como un descuadre. Ver `amountBreakdown`.
    const aclaraciones = amountBreakdown(visibles, spec, valores);

    return (
      <section key={`${grupo.label ?? 'bloque'}-${indice}`} className="mb-6 last:mb-0">
        {grupo.label !== null && grupo.label !== '' && (
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-faint">{grupo.label}</h3>
        )}
        <div
          className={clsx(
            'grid gap-x-6 gap-y-4',
            grupo.columns >= 3 ? 'sm:grid-cols-3' : grupo.columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-1',
          )}
        >
          {visibles.map((nombre) => renderCampo(nombre, aclaraciones[nombre] ?? null))}
        </div>
      </section>
    );
  };

  const hayAvanzados = (grupos: FieldGroup[]): boolean =>
    grupos.some((grupo) => splitByPriority(spec, sinEstado(grupo.fields)).avanzados.length > 0);

  const hayBasicos = (grupos: FieldGroup[]): boolean =>
    grupos.some((grupo) => splitByPriority(spec, sinEstado(grupo.fields)).basicos.length > 0);

  /**
   * Una solapa cuyos campos son TODOS avanzados se abre vacia: el panel no
   * dibuja nada y lo unico visible es un «Opciones avanzadas» plegado. Le pasaba
   * a «Observaciones» del periodo de expensas —una solapa con su nombre, un
   * clic, y adentro nada— y a la del gasto, que tiene un solo campo.
   *
   * La divulgacion progresiva existe para que 8 campos no parezcan 40; no tiene
   * ningun sentido cuando lo que esconde es el contenido ENTERO de la seccion:
   * ahi no simplifica, esconde. Asi que si no hay nada basico que mostrar, los
   * avanzados se muestran de una y no se dibuja el desplegable.
   */
  const desplegarTodo = (grupos: FieldGroup[]): boolean =>
    !hayBasicos(grupos) && hayAvanzados(grupos);

  return (
    /*
      El guardado de la ficha, disponible para todo lo que cuelga de ella sin
      pasarlo por prop a través de seis niveles: la grilla de renglones, el panel
      de venta rápida, cualquier panel de solapa. Es lo que hace que la salida
      sea LA MISMA en los dos lados del defecto —los botones de la cabecera y los
      renglones que exigen guardar primero— en vez de dos soluciones parecidas.
    */
    <ProveedorDeGuardado estado={estadoDeGuardado}>
    <form
      ref={formulario}
      noValidate
      onSubmit={(evento) => {
        evento.preventDefault();
        enviar();
      }}
    >
      {/* Cabecera */}
      <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        {/* El título tiene piso propio (`sm:basis-64`) y prioridad para crecer.
            Sin eso, el bloque de botones —que mide lo que miden sus botones— se
            quedaba con todo el ancho y al `<h1>` le tocaban las sobras: medido a
            1536 px en un período emitido, 174 px para un nombre de 185, o sea
            «Expensas 07/2…». Saber qué registro se está mirando no es negociable
            contra un botón más. */}
        <div className="min-w-0 sm:flex-1 sm:basis-64">
          <h1 className="truncate text-2xl font-semibold tracking-tight" title={titulo}>
            {titulo}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-fg-muted">
            <span>{spec.label}</span>
            {archivado && <Badge tone="warning">Archivado</Badge>}
            {sucios.size > 0 && <Badge tone="accent">Cambios sin guardar</Badge>}
          </p>
        </div>

        {/* La botonera NO puede llevar `shrink-0`: con `shrink-0` el ancho base
            del bloque es el de todos los botones en una sola fila, asi que
            `flex-wrap` no llega a plegar nada y lo que sobra se sale de la
            pantalla. Dejandolo encoger, `flex-wrap` pliega de verdad y
            `sm:justify-end` mantiene las filas alineadas a la derecha. */}
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
          {/* Guardar y Descartar quedan SIEMPRE afuera del menú: son lo que se
              usa a cada rato, y esconderlos para ordenar la fila sería cambiar
              un problema chico por uno serio. */}
          {!soloLectura && (
            <Button type="submit" variant="primary" loading={guardar.isPending}>
              {esNuevo ? 'Crear' : 'Guardar'}
            </Button>
          )}
          {/* Sin permiso de escritura NO hay botón de guardar y todos los campos
              se dibujan como texto plano. Hasta acá eso pasaba sin una sola
              palabra: la pantalla parecía un formulario roto en vez de una
              consulta. Se dice al lado del lugar donde iría el botón, que es la
              misma solución que el panel de usuarios ya usaba para el caso
              idéntico. */}
          {soloLectura && (
            <p className="text-sm text-fg-muted" role="status">
              {esNuevo
                ? `Tu usuario puede mirar esta pantalla pero no dar de alta ${spec.label_plural.toLowerCase()}.`
                : `Tu usuario puede mirar esta pantalla pero no modificar ${spec.label_plural.toLowerCase()}.`}
            </p>
          )}
          {sucios.size > 0 && (
            <Button
              variant="ghost"
              onClick={() => {
                setValores(esNuevo ? valoresIniciales(spec, context) : { ...(registroServidor ?? {}) });
                setSucios(new Set());
                setErrores({});
                setMotivoDelRechazo(null);
              }}
            >
              Descartar
            </Button>
          )}
          {/* Todo lo demás —acciones de servidor, imprimir, mandar por mail,
              archivar y eliminar— entra en una sola botonera, en ese orden de
              importancia: los primeros a la vista, el resto en «Más». */}
          <Toolbar
            items={botonesDeLaCabecera}
            focusKey={focoAlVolver}
            onEnfocado={() => {
              setFocoAlVolver(null);
            }}
          />
        </div>
      </header>

      {/* Los botones que hoy no están y por qué. Va inmediatamente debajo de la
          botonera —donde la persona los estaba buscando— y ocupa una línea
          mientras nadie la abra. Ver `AccionesNoDisponibles`. */}
      {esRegistro && <AccionesNoDisponibles acciones={noDisponibles} />}

      {/* Por qué el servidor rechazó el guardado, cuando el motivo no es de
          ningún campo. Va acá —debajo de la botonera y arriba de los campos—
          porque es donde mira quien acaba de apretar «Crear» y no pasó nada.
          `role="alert"` para que un lector de pantalla lo anuncie: la persona
          que no ve la pantalla tenía todavía menos rastro que las demás. */}
      {motivoDelRechazo !== null && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-fg"
        >
          <span className="font-medium">No se pudo guardar. </span>
          {motivoDelRechazo}
        </p>
      )}

      {/* Diálogos y paneles de resultado de las tres barras. Van fuera de la fila
          de botones: no ocupan lugar y no deben desmontarse cuando el botón que
          los abrió se pliega dentro del menú. */}
      {esRegistro && (
        <>
          {accionesDeServidor.dialogs}
          {reportes.dialogs}
          {correo.dialogs}
        </>
      )}

      {/* Botones inteligentes: la fila de tarjetas-contador hacia los registros
          relacionados, filtrados por este registro. Sólo aparece sobre un
          registro guardado (uno nuevo no tiene relacionados que contar). */}
      <SmartButtons
        buttons={botonesInteligentes}
        model={spec.model}
        recordId={registroServidor?.id}
        onNavegar={guardia.navegarConAviso}
      />

      {/* Estado en la cabecera: semáforo, no desplegable */}
      {campoEstado !== null && spec.fields[campoEstado] !== undefined && (
        <CabeceraEstado
          spec={spec}
          campo={campoEstado}
          valor={valores[campoEstado] ?? null}
          readonly={soloLectura}
          recordId={idRegistro}
          onChange={(valor) => {
            cambiar(campoEstado, valor);
          }}
        />
      )}

      {/* Por que hay campos bloqueados. Ver `bloqueadoPorDominio`. */}
      {motivosDeBloqueo.map((texto) => (
        <p
          key={texto}
          role="status"
          className="mb-4 rounded-lg border border-line bg-app-subtle px-4 py-3 text-sm text-fg-muted"
        >
          {texto}
        </p>
      ))}

      <div className="rounded-xl border border-line bg-surface p-4 sm:p-6">
        {gruposPrincipales.map((grupo, indice) => renderGrupo(grupo, indice, false))}

        {hayAvanzados(gruposPrincipales) && (
          <div className="mt-2 border-t border-line pt-4">
            <button
              type="button"
              aria-expanded={avanzadasAbiertas}
              aria-controls="opciones-avanzadas"
              onClick={() => {
                setAvanzadasAbiertas((actual) => !actual);
              }}
              className="text-sm font-medium text-accent hover:underline"
            >
              {avanzadasAbiertas ? 'Ocultar opciones avanzadas' : 'Opciones avanzadas'}
            </button>
            {avanzadasAbiertas && (
              <div id="opciones-avanzadas" className="mt-4">
                {gruposPrincipales.map((grupo, indice) => renderGrupo(grupo, indice, true))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Solapas */}
      {solapas.length > 0 && (
        <div className="mt-6 rounded-xl border border-line bg-surface">
          <div role="tablist" aria-label="Secciones" className="flex gap-1 overflow-x-auto border-b border-line px-2 pt-2">
            {solapas.map((tab, indice) => {
              // Una solapa que esconde un campo con error se marca. El panel de
              // al lado está `hidden`, así que sin esta señal el usuario no
              // tiene forma de saber que el problema está ahí adentro.
              const conError = Object.keys(errores).some((campo) => solapaDelCampo(campo) === indice);
              return (
              <button
                key={tab.label}
                type="button"
                role="tab"
                id={`solapa-${indice}`}
                aria-selected={solapa === indice}
                aria-controls={`panel-${indice}`}
                tabIndex={solapa === indice ? 0 : -1}
                onClick={() => {
                  setSolapa(indice);
                }}
                onKeyDown={(evento) => {
                  if (evento.key === 'ArrowRight') setSolapa((actual) => (actual + 1) % solapas.length);
                  if (evento.key === 'ArrowLeft') setSolapa((actual) => (actual - 1 + solapas.length) % solapas.length);
                }}
                className={clsx(
                  'shrink-0 rounded-t-md px-4 py-2.5 text-sm font-medium transition-colors',
                  solapa === indice
                    ? 'border-b-2 border-accent text-accent'
                    : 'border-b-2 border-transparent text-fg-muted hover:text-fg',
                  conError && solapa !== indice && 'text-danger',
                )}
              >
                {tab.label}
                {conError && (
                  <span className="ml-1.5 text-danger" aria-label="tiene campos sin completar">
                    •
                  </span>
                )}
              </button>
              );
            })}
          </div>

          {solapas.map((tab, indice) => (
            <div
              key={tab.label}
              id={`panel-${indice}`}
              role="tabpanel"
              aria-labelledby={`solapa-${indice}`}
              hidden={solapa !== indice}
              tabIndex={0}
              className="p-4 sm:p-6"
            >
              {/* Panel propio de la solapa, si lo tiene. La consulta es
                  GENERICA —«¿hay panel para (modelo, solapa)?»— y este renderer
                  no sabe que modelos hay en la tabla ni que dibujan: la lista
                  vive en `tab-panels.tsx`, igual que los widgets viven en el
                  registro de campos. Va ARRIBA de los bloques porque un panel de
                  solapa es su encabezado (los totales del cliente) y los campos
                  son el detalle. */}
              {(solapa === indice || visitadas.has(indice)) && (
                <>
                  <TabPanel model={spec.model} tabLabel={tab.label} recordId={idRegistro} />
                  {tab.groups.map((grupo, posicion) => renderGrupo(grupo, posicion, desplegarTodo(tab.groups)))}
                  {hayAvanzados(tab.groups) && !desplegarTodo(tab.groups) && (
                    <details className="mt-2 border-t border-line pt-4">
                      <summary className="cursor-pointer text-sm font-medium text-accent">Opciones avanzadas</summary>
                      <div className="mt-4">{tab.groups.map((grupo, posicion) => renderGrupo(grupo, posicion, true))}</div>
                    </details>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Adjuntos: los archivos que cuelgan de este registro. Aparece si el
          modelo los declaro (`ModelSpec.attachments`) y solo sobre un registro
          guardado: sin id no hay de que colgar el papel. */}
      {spec.attachments === true && idRegistro !== null && (
        <AttachmentsPanel modelName={spec.model} recordId={idRegistro} />
      )}

      {/* Hilo de seguimiento: quien hizo que y por que. Aparece si el modelo
          hereda `ChatterMixin` en el backend (`spec.chatter`, que se DEDUCE de la
          herencia y no se declara) y solo sobre un registro guardado: la historia
          de algo que todavia no existe esta vacia por definicion. */}
      {spec.chatter === true && idRegistro !== null && (
        <ChatterPanel modelName={spec.model} recordId={idRegistro} />
      )}
      {/* En un alta, las secciones de abajo —botones inteligentes, adjuntos y
          seguimiento— simplemente no se dibujaban. El motivo es impecable (sin
          id no hay de qué colgar un archivo, qué historia contar ni qué
          relacionados contar) y era invisible: quien venía de una ficha
          guardada veía una pantalla distinta y no sabía si se rompió, si le
          falta un permiso o si tiene que guardar primero. Se dice una vez, en
          el lugar donde esas secciones van a aparecer, y con el mismo botón que
          ofrecen la grilla de renglones y el panel de venta rápida. */}
      {idRegistro === null && faltaPorCrear.length > 0 && (
        <AvisoDeBloqueo
          className="mt-6"
          motivo={`Todavía no está creado, así que esta ficha no muestra ${listaEnCastellano(faltaPorCrear)}. Aparece en cuanto lo crees.`}
          salida={{
            etiqueta: 'Crear',
            aclaracion: 'Se crea el registro y nada más: nada se emite ni se factura por esto.',
            ejecutar: () => {
              estadoDeGuardado.guardar();
            },
            ...(guardar.isPending ? { corriendo: true } : {}),
          }}
        />
      )}

      <ConfirmDialog
        open={confirmarBorrado}
        tone="danger"
        title={`¿Eliminar ${spec.label.toLowerCase()}?`}
        description="La eliminación es definitiva y no queda registro. Si solo querés sacarlo de la vista, archivalo: se puede restaurar."
        confirmLabel="Eliminar definitivamente"
        loading={borrar.isPending}
        error={errorDeBorrado?.motivo ?? null}
        canRetry={!(errorDeBorrado?.definitivo ?? false)}
        exit={
          // La salida se ofrece SÓLO cuando el borrado ya falló y falló para
          // siempre. Ponerla antes sería empujar a archivar algo que se puede
          // borrar, que es una decisión de la persona y no de la pantalla.
          errorDeBorrado?.definitivo === true && sePuedeArchivar
            ? {
                label: 'Archivar',
                hint: 'Deja de aparecer en las listas y la contabilidad queda intacta. Se puede restaurar.',
                onClick: () => {
                  archivar.mutate(true);
                },
                loading: archivar.isPending,
              }
            : null
        }
        onConfirm={() => {
          borrar.mutate();
        }}
        onCancel={() => {
          setConfirmarBorrado(false);
          setErrorDeBorrado(null);
        }}
      />

      <ConfirmDialog
        open={guardia.destinoPendiente !== null}
        tone="danger"
        title="Tenés cambios sin guardar"
        description="Si salís ahora se pierden. Podés volver, guardar y después seguir."
        confirmLabel="Salir sin guardar"
        cancelLabel="Seguir editando"
        onConfirm={guardia.confirmar}
        onCancel={guardia.cancelar}
      />
    </form>
    </ProveedorDeGuardado>
  );
}

// ---------------------------------------------------------------------------
// Estado en la cabecera
// ---------------------------------------------------------------------------
/**
 * Campo de estado dibujado arriba de todo.
 *
 * Se usa el mismo widget que en el resto del formulario (el registro decide
 * cuál), pero sin etiqueta visible: el estado se entiende solo, y repetir
 * "Estado:" delante de "Borrador / Confirmado / Pagado" es ruido. La etiqueta
 * sigue existiendo para los lectores de pantalla.
 */
function CabeceraEstado({
  spec,
  campo,
  valor,
  readonly,
  recordId,
  onChange,
}: {
  spec: ModelSpec;
  campo: string;
  valor: FieldValue;
  readonly: boolean;
  recordId: number | null;
  onChange: (valor: FieldValue) => void;
}) {
  const meta = spec.fields[campo];
  if (meta === undefined) return null;
  const Widget = getFieldInput(meta);
  const id = `campo-${campo}`;

  return (
    <div className="mb-4 flex items-center gap-3 rounded-xl border border-line bg-surface px-4 py-3">
      <span id={`${id}-label`} className="text-sm font-medium text-fg-muted">
        {meta.label}
      </span>
      <Widget
        id={id}
        name={campo}
        meta={meta}
        value={valor}
        onChange={onChange}
        readonly={readonly || meta.readonly}
        required={meta.required}
        invalid={false}
        context={{ modelName: spec.model, recordId }}
      />
    </div>
  );
}
